import type { Trip } from '@viaja/contracts';
import { Button, Chip, Price } from '@viaja/design-system';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tripPeriod } from '../../lib/format';
import { useDeleteTrip, useRoutes, useSaveTrip, useTrip } from '../../lib/queries';
import { SubScreen } from '../../ui/AppShell';
import { Sheet } from '../../ui/Sheet';
import { LoadError, Loading, Notice } from '../../ui/feedback';
import { RouteItem } from '../routes/RouteItem';
import { TripFormSheet } from './TripFormSheet';
import { TRIP_STATUS_LABEL } from './TripsPage';

export function TripPage() {
  const { id = '' } = useParams();
  const trip = useTrip(id);
  return (
    <SubScreen title="Viagem" back="/viagens">
      {trip.isPending ? (
        <Loading label="Abrindo a viagem…" />
      ) : trip.error ? (
        <LoadError message={trip.error.message} onRetry={() => trip.refetch()} />
      ) : (
        <TripPanel trip={trip.data} />
      )}
    </SubScreen>
  );
}

function TripPanel({ trip }: { trip: Trip }) {
  const navigate = useNavigate();
  const routes = useRoutes(trip.id);
  const save = useSaveTrip(trip.id);
  const remove = useDeleteTrip();
  const [sheet, setSheet] = useState<'edit' | 'delete' | null>(null);
  const planning = trip.status === 'planejando';

  return (
    <>
      <div className="app-head">
        <h1 className="display-title">{trip.name}</h1>
        <p className="small app-meta">
          {tripPeriod(trip.startDate, trip.endDate)} · {TRIP_STATUS_LABEL[trip.status]}
        </p>
        {trip.notes ? <p className="body">{trip.notes}</p> : null}
      </div>

      {trip.bestTotal ? (
        <section className="app-section" aria-label="Total da viagem">
          <span className="eyebrow vc-eyebrow">Total hoje, somando as rotas</span>
          <Price value={trip.bestTotal} size="md" />
        </section>
      ) : null}

      <section className="app-section" aria-labelledby="rotas-viagem">
        <h2 id="rotas-viagem" className="display-section">
          Rotas
        </h2>
        {routes.isPending ? (
          <Loading />
        ) : routes.error ? (
          <Notice>{routes.error.message}</Notice>
        ) : routes.data.length === 0 ? (
          <p className="body app-meta">Nenhuma rota nessa viagem ainda.</p>
        ) : (
          <ul className="app-list">
            {routes.data.map((r) => (
              <RouteItem key={r.id} route={r} />
            ))}
          </ul>
        )}
      </section>

      <section className="app-section" aria-label="Ações da viagem">
        {save.error ? <Notice>{save.error.message}</Notice> : null}
        <div className="app-wrap">
          <Chip icon="calendario" onClick={() => setSheet('edit')}>
            Editar
          </Chip>
          {planning ? (
            <Chip icon="check" onClick={() => save.mutate({ status: 'comprada' })}>
              Comprei as passagens
            </Chip>
          ) : (
            <Chip icon="rota" onClick={() => save.mutate({ status: 'planejando' })}>
              Voltar a planejar
            </Chip>
          )}
          {trip.status !== 'arquivada' ? (
            <Chip icon="pausar" onClick={() => save.mutate({ status: 'arquivada' })}>
              Arquivar
            </Chip>
          ) : null}
          <Chip icon="fechar" onClick={() => setSheet('delete')}>
            Apagar
          </Chip>
        </div>
        {!planning ? (
          <p className="caption app-meta">Viagem fora do planejamento: as rotas dela ficam pausadas.</p>
        ) : null}
      </section>

      {planning ? (
        <div className="app-actionbar">
          <Button icon="mais" block onClick={() => navigate(`/rotas/nova?viagem=${trip.id}`)}>
            Monitorar uma rota
          </Button>
        </div>
      ) : null}

      <TripFormSheet open={sheet === 'edit'} trip={trip} onClose={() => setSheet(null)} />
      <Sheet open={sheet === 'delete'} title="Apagar esta viagem?" onClose={() => setSheet(null)}>
        <p className="body">As rotas continuam monitoradas, só deixam de fazer parte da viagem.</p>
        <Button
          block
          disabled={remove.isPending}
          onClick={() => remove.mutate(trip.id, { onSuccess: () => navigate('/viagens', { replace: true }) })}
        >
          Apagar viagem
        </Button>
        <Button variant="secondary" block onClick={() => setSheet(null)}>
          Manter
        </Button>
      </Sheet>
    </>
  );
}
